import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './Books.css';

// Books (October 2026): billing, AJ and Monica only (server enforces it in routes/books.js).
// One row per billing company (the customer that pays); its stations are CRM companies.
// Phase 1-2: companies, prices, discounts, QuickBooks connection, customer matching, invoice and
// payment statuses read from QuickBooks. Invoice drafts and holds come later.
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = n => (n == null || n === '' ? '' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 }));
// When an approved invoice goes out: the 9 AM Eastern run on its date, or the next run if that's passed.
const sendWhen = d => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date()).map(p => [p.type, p.value]));
  const today = `${parts.year}-${parts.month}-${parts.day}`;
  const day = s => new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const date = String(d).slice(0, 10);
  if (date > today) return { long: `sends ${day(date)} at 9 AM Eastern`, short: `sends ${day(date)}, 9 AM ET` };
  if (Number(parts.hour) < 9) return { long: 'sends today at 9 AM Eastern', short: 'sends today, 9 AM ET' };
  const t = new Date(today + 'T12:00:00'); t.setDate(t.getDate() + 1);
  return { long: `sends tomorrow (${day(t.toISOString().slice(0, 10))}) at 9 AM Eastern`, short: 'sends tomorrow, 9 AM ET' };
};
const fmtDate = d => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const ordinal = n => (n ? n + (['th', 'st', 'nd', 'rd'][(n % 100 - 20) % 10] || ['th', 'st', 'nd', 'rd'][n % 100] || 'th') : '');
// Today in US Eastern (billing runs on Eastern time), not UTC.
const todayStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

function payState(c) {
  if (c.unpaid) {
    const late = c.unpaid.oldest_due && String(c.unpaid.oldest_due).slice(0, 10) < todayStr();
    return { kind: late ? 'late' : 'open', text: `${money(c.unpaid.owed)} not received`, sub: `${c.unpaid.n} invoice${c.unpaid.n === 1 ? '' : 's'}${late ? ` · oldest due ${fmtDate(c.unpaid.oldest_due)}` : ''}` };
  }
  if (c.latest_payment) return { kind: 'rec', text: 'Received', sub: `${fmtDate(c.latest_payment.paid_on)} · ${money(c.latest_payment.amount)}${c.latest_payment.method ? ' · ' + c.latest_payment.method : ''}` };
  if (c.latest_invoice) return { kind: 'open', text: 'No payment yet', sub: '' };
  return { kind: 'none', text: 'No invoices', sub: '' };
}

const FILTERS = [
  ['all', 'All', () => true],
  ['approve', 'To approve', c => (c.pending || []).some(p => p.status === 'draft' || p.status === 'created'), 'warn'],
  ['hold', 'On hold', c => !!c.hold_started_at, 'bad'],
  ['live', 'Live', c => c.status === 'Live'],
  ['unpaid', 'Not received', c => !!c.unpaid, 'warn'],
  ['late', 'Overdue', c => payState(c).kind === 'late', 'bad'],
  ['noprice', 'No price', c => c.plan_price == null && ['Live', 'Onboarding'].includes(c.status), 'bad'],
  ['nomatch', 'Not matched to QuickBooks', c => !c.qbo_customer_id, 'warn'],
  ['paused', 'Paused / Cancelled', c => ['Paused', 'Cancelled'].includes(c.status)],
];

export default function Books({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const [panel, setPanel] = useState(null); // 'company' | 'setup' | 'log'
  const [banner, setBanner] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/books/overview`, auth());
      setData(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.code === 'mfa_required' ? 'mfa' : (e.response?.data?.error || 'Could not load Books.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // "CC saved" checkbox: ticked in the table, saved straight away (shown at once, undone if the save fails).
  const setCcSaved = async (c, on) => {
    setData(d => ({ ...d, companies: d.companies.map(x => (x.id === c.id ? { ...x, cc_saved: on } : x)) }));
    try { await axios.patch(`${API_URL}/api/books/companies/${c.id}`, { cc_saved: on }, auth()); }
    catch (e) {
      setData(d => ({ ...d, companies: d.companies.map(x => (x.id === c.id ? { ...x, cc_saved: !on } : x)) }));
      setBanner({ tone: 'bad', text: e.response?.data?.error || `Could not save CC saved for ${c.name}.` });
    }
  };

  // Return from Intuit: ?qbo=connected|error
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get('qbo');
    if (!s) return;
    const reasons = { expired: 'The connection link expired. Click Connect QuickBooks again.', not_allowed: 'Only AJ and Monica can connect QuickBooks.', exchange: 'QuickBooks did not accept the connection. Check the keys in Railway and try again.', access_denied: 'The connection was cancelled in QuickBooks.' };
    setBanner(s === 'connected'
      ? { tone: 'good', text: 'QuickBooks connected. Importing customers, invoices and payments now; this takes a minute.' }
      : { tone: 'bad', text: reasons[p.get('reason')] || `QuickBooks connection failed (${p.get('reason') || 'unknown'}).` });
    window.history.replaceState({}, '', '/books');
    if (s === 'connected') setTimeout(load, 15000);
  }, [load]);

  const companies = useMemo(() => data?.companies || [], [data]);
  const fn = FILTERS.find(f => f[0] === filter)[2];
  const list = companies.filter(fn).filter(c => !q || (c.name + ' ' + c.stations.map(s => s.name).join(' ') + ' ' + (c.qbo_customer?.display_name || '')).toLowerCase().includes(q.toLowerCase()))
    // Billing day 1st first, then 2nd, ... (user, 3 Nov 2026); no billing day last; same day by name.
    .sort((x, y) => (x.billing_day || 99) - (y.billing_day || 99) || x.name.localeCompare(y.name));
  const open = companies.find(c => c.id === openId) || null;

  const stats = useMemo(() => {
    const live = companies.filter(c => c.status === 'Live');
    const owed = companies.reduce((s, c) => s + (c.unpaid?.owed || 0), 0);
    const late = companies.filter(c => payState(c).kind === 'late').reduce((s, c) => s + (c.unpaid?.owed || 0), 0);
    return { plans: live.reduce((s, c) => s + (c.plan_price || 0), 0), live: live.length, owed, late };
  }, [companies]);

  const connect = async () => {
    setBusy('connect');
    try {
      const r = await axios.get(`${API_URL}/api/books/qbo/connect`, auth());
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ tone: 'bad', text: e.response?.data?.error || 'Could not start the connection.' });
      setBusy('');
    }
  };
  const sync = async full => {
    setBusy('sync');
    try {
      const r = await axios.post(`${API_URL}/api/books/qbo/sync`, { full }, auth());
      const c = r.data.counts || {};
      setBanner({ tone: 'good', text: `Synced: ${['Customer', 'Item', 'Invoice', 'Payment'].map(k => `${c[k] || 0} ${k === 'Item' ? 'products' : k.toLowerCase() + 's'}`).join(', ')} ${full ? 'read' : 'changed'}.` });
      await load();
    } catch (e) {
      setBanner({ tone: 'bad', text: e.response?.data?.error || 'Sync failed. See the sync log.' });
    } finally {
      setBusy('');
    }
  };
  const disconnect = async () => {
    setBusy('disconnect');
    try { await axios.post(`${API_URL}/api/books/qbo/disconnect`, {}, auth()); await load(); setBanner({ tone: 'good', text: 'QuickBooks disconnected.' }); }
    catch (e) { setBanner({ tone: 'bad', text: 'Could not disconnect.' }); }
    finally { setBusy(''); }
  };

  const readyCount = companies.reduce((n, c) => n + (c.pending || []).filter(p => p.status === 'draft' && p.ready && !p.adsCheck).length, 0);
  const buildDrafts = async () => {
    setBusy('build');
    try {
      const r = await axios.post(`${API_URL}/api/books/drafts/build`, {}, auth());
      const sk = r.data.skipped || [];
      setBanner({ tone: sk.length ? 'warn' : 'good', text: `${r.data.created.length} invoice draft${r.data.created.length === 1 ? '' : 's'} prepared for the next 7 days.${sk.length ? ` Not prepared: ${sk.map(x => `${x.company} (${x.why})`).join('; ')}.` : ''}` });
      await load();
    } catch (e) { setBanner({ tone: 'bad', text: e.response?.data?.error || 'Could not prepare invoices.' }); }
    finally { setBusy(''); }
  };
  const approveAll = async () => {
    setBusy('approve');
    try {
      const r = await axios.post(`${API_URL}/api/books/invoices/approve-all`, {}, auth());
      setBanner({ tone: r.data.held.length ? 'warn' : 'good', text: `Approved ${r.data.approved}. They go out on their billing day at 9 AM Eastern.${r.data.held.length ? ` Still need you: ${r.data.held.map(h => `${h.company} (${h.why})`).join('; ')}.` : ''}` });
      await load();
    } catch (e) { setBanner({ tone: 'bad', text: e.response?.data?.error || 'Could not approve.' }); }
    finally { setBusy(''); }
  };

  const qb = data?.qbo;
  return (
    <div className="bk-page">
      <SharedNav user={user} onLogout={onLogout} />
      <div className="bk-body">
        <div className="bk-row1">
          <h1>Books <small>AJ and Monica only</small></h1>
          {qb && (
            <div className="bk-qbo">
              {!qb.configured && <span className="bk-pill bad">QuickBooks keys not set in Railway</span>}
              {qb.configured && qb.status === 'connected' && <>
                <span className="bk-pill good">QuickBooks connected{qb.environment === 'sandbox' ? ' (sandbox)' : ''}</span>
                <span className="bk-muted">{qb.last_sync_at ? `synced ${new Date(qb.last_sync_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })} IST` : 'first sync running'}</span>
                <button className="bk-btn" disabled={!!busy} onClick={() => sync(false)}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</button>
                <button className="bk-btn" disabled={!!busy} onClick={() => sync(true)} title="Re-read every customer, invoice and payment">Full sync</button>
                <button className="bk-btn ghost" disabled={!!busy} onClick={disconnect}>Disconnect</button>
              </>}
              {qb.configured && qb.status !== 'connected' && <>
                {qb.status === 'reconnect' && <span className="bk-pill bad">Connection lost: {qb.last_error || 'reconnect needed'}</span>}
                <button className="bk-btn primary" disabled={!!busy} onClick={connect}>{qb.status === 'reconnect' ? 'Reconnect QuickBooks' : `Connect QuickBooks${qb.environment === 'sandbox' ? ' (sandbox)' : ''}`}</button>
              </>}
              <button className="bk-link" onClick={() => setPanel('log')}>Sync log</button>
              <button className="bk-link" onClick={() => setPanel('settings')}>Holds &amp; reminders</button>
            </div>
          )}
        </div>

        {banner && <div className={`bk-banner ${banner.tone}`}>{banner.text}<button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button></div>}
        {error === 'mfa' && <div className="bk-banner warn"><span><b>Books needs two-factor sign-in.</b> Log out and log in again; you'll be asked to set it up with your authenticator app (takes a minute).</span><button className="bk-btn primary" onClick={onLogout}>Log out</button></div>}
        {error && error !== 'mfa' && <div className="bk-banner bad">{error}</div>}

        {data && data.unassigned.length > 0 && (
          <div className="bk-banner warn">
            <span><b>{data.unassigned.length} CRM accounts</b> aren't in a billing company yet, so they can't be invoiced.</span>
            <button className="bk-btn primary" onClick={() => setPanel('setup')}>Set up companies</button>
          </div>
        )}

        {data && <>
          <div className="bk-stats">
            <span><b>{money(stats.plans)}</b> monthly plans · {stats.live} Live companies</span>
            <span className={stats.owed ? 'warn' : ''}><b>{money(stats.owed) || '$0'}</b> not received</span>
            <span className={stats.late ? 'bad' : ''}><b>{money(stats.late) || '$0'}</b> overdue</span>
          </div>
          <div className="bk-row2">
            <div className="bk-chips">
              {FILTERS.map(([id, label, f, tone]) => (
                <button key={id} className={`bk-chip ${tone || ''} ${filter === id ? 'on' : ''}`} onClick={() => setFilter(id)}>
                  {label}<span className="c">{companies.filter(f).length}</span>
                </button>
              ))}
            </div>
            <div className="bk-acts2">
              <button className="bk-btn" disabled={!!busy} onClick={buildDrafts}>{busy === 'build' ? 'Preparing…' : 'Prepare invoices'}</button>
              <button className="bk-btn primary" disabled={!!busy || !readyCount} onClick={approveAll}>{busy === 'approve' ? 'Approving…' : `Approve all ready (${readyCount})`}</button>
              <input className="bk-search" type="search" placeholder="Search company, station or QuickBooks name" value={q} onChange={e => setQ(e.target.value)} aria-label="Search" />
            </div>
          </div>

          <div className="bk-tablewrap">
            <table className="bk-table">
              <thead><tr><th>Company</th><th className="n">Plan / mo</th><th>Billing day</th><th>Indeed ads</th><th>QuickBooks customer</th><th>Invoice date</th><th>Invoice status</th><th>Payment status</th><th>Last payment</th><th>Next billing</th><th>Status</th><th>CC saved</th><th className="fill"></th></tr></thead>
              <tbody>
                {list.map(c => {
                  return (
                    <tr key={c.id} className={openId === c.id ? 'sel' : ''} onClick={() => { setOpenId(c.id); setPanel('company'); }} tabIndex={0} onKeyDown={e => e.key === 'Enter' && (setOpenId(c.id), setPanel('company'))}>
                      <td className="co">{c.name}</td>
                      <td className="n">{c.plan_price != null ? <b>{money(c.plan_price)}</b> : <span className="bk-flag">Not set</span>}</td>
                      <td>{ordinal(c.billing_day) || <span className="bk-muted">not set</span>}</td>
                      <td>{c.charge_ads ? <span className="bk-pill blue">Charged</span> : <span className="bk-muted">No</span>}</td>
                      <td>{c.qbo_customer ? c.qbo_customer.display_name : c.qbo_suggestion ? <span className="bk-muted">Suggested: {c.qbo_suggestion.display_name}</span> : <span className="bk-flag">Not matched</span>}</td>
                      <CycleCells c={c} />
                      <td><span className={`bk-pill ${c.status === 'Live' ? 'good' : c.status === 'Onboarding' ? 'blue' : c.status === 'Paused' ? 'warn' : 'grey'}`}>{c.status}</span>{c.hold_started_at && <span className="bk-pill bad" style={{ marginLeft: 4 }}>On hold</span>}</td>
                      <td className="bk-cc" onClick={e => e.stopPropagation()}><input type="checkbox" checked={!!c.cc_saved} aria-label={`Card saved in QuickBooks for ${c.name}`} onChange={e => setCcSaved(c, e.target.checked)} /></td>
                      <td className="fill"></td>
                    </tr>
                  );
                })}
                {!list.length && <tr><td colSpan={13} className="bk-empty">{companies.length ? 'Nothing matches this filter.' : 'No billing companies yet. Click "Set up companies" above.'}</td></tr>}
              </tbody>
            </table>
          </div>
        </>}
        <div className="bk-support">Problems with Books or QuickBooks? Contact hiring@mountainrecruiting.com</div>
      </div>

      {panel && <div className="bk-scrim" onClick={() => setPanel(null)} />}
      {panel === 'company' && open && <CompanyPanel c={open} data={data} onClose={() => setPanel(null)} reload={load} />}
      {panel === 'setup' && data && <SetupPanel data={data} onClose={() => setPanel(null)} reload={load} />}
      {panel === 'log' && <LogPanel onClose={() => setPanel(null)} />}
      {panel === 'settings' && <SettingsPanel onClose={() => setPanel(null)} reload={load} />}
    </div>
  );
}

function Panel({ title, sub, onClose, children, foot }) {
  useEffect(() => {
    const k = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <aside className="bk-panel" aria-label={title}>
      <div className="bk-ph"><div><h2>{title}</h2>{sub && <div className="bk-muted">{sub}</div>}</div><button className="bk-btn ghost" onClick={onClose} aria-label="Close">✕</button></div>
      <div className="bk-pb">{children}</div>
      {foot && <div className="bk-pf">{foot}</div>}
    </aside>
  );
}

function CompanyPanel({ c, data, onClose, reload }) {
  const [tab, setTab] = useState(() => ((c.pending || []).length ? 'review' : 'settings'));
  const [form, setForm] = useState({});
  const [stationIds, setStationIds] = useState(c.stations.map(s => s.id));
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inv, setInv] = useState(null);
  const [adj, setAdj] = useState({ kind: 'one_time', amount: '', description: '', until_date: '' });

  useEffect(() => {
    setForm({
      name: c.name, plan_price: c.plan_price ?? '', billing_day: c.billing_day ?? '', charge_ads: !!c.charge_ads, ad_budget: c.ad_budget ?? '',
      bill_email: c.bill_email || '', cc_emails: c.cc_emails || '', terms: c.terms || 'Due on receipt', hold_policy: c.hold_policy || 'auto',
      qbo_customer_id: c.qbo_customer_id || '', notes: c.notes || '',
    });
    setStationIds(c.stations.map(s => s.id)); setMsg(null); setInv(null);
  }, [c]);
  useEffect(() => {
    if (tab !== 'invoices' || inv) return;
    axios.get(`${API_URL}/api/books/companies/${c.id}/invoices`, auth()).then(r => setInv(r.data)).catch(() => setInv({ invoices: [], payments: [] }));
  }, [tab, inv, c.id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await axios.patch(`${API_URL}/api/books/companies/${c.id}`, form, auth());
      const rb = r.data?.rebuilt || [];
      const before = c.stations.map(s => s.id).sort().join(',');
      if ([...stationIds].sort().join(',') !== before) await axios.put(`${API_URL}/api/books/companies/${c.id}/stations`, { station_ids: stationIds }, auth());
      const made = (r.data?.created || []).filter(x => !rb.some(y => y.id === x.id));
      const skip = r.data?.skipped || [];
      setMsg({ tone: 'good', text: 'Saved.' + rb.map(x => (x.to ? ` Draft for ${fmtDate(x.from)} replaced with ${fmtDate(x.to)}.` : ` Draft for ${fmtDate(x.from)} removed (QuickBooks already has an invoice near the new date).`)).join('')
        + made.map(x => ` Draft for ${fmtDate(x.date)} created.`).join('') + skip.map(x => ` No draft for ${fmtDate(x.date)}: ${x.why}.`).join('') });
      await reload();
    } catch (e) {
      setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };
  const addAdj = async () => {
    setMsg(null);
    try {
      await axios.post(`${API_URL}/api/books/companies/${c.id}/adjustments`, adj, auth());
      setAdj({ kind: 'one_time', amount: '', description: '', until_date: '' });
      await reload();
    } catch (e) { setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not add it.' }); }
  };
  const delAdj = async id => { await axios.delete(`${API_URL}/api/books/adjustments/${id}`, auth()).catch(() => {}); await reload(); };
  const remove = async () => {
    setMsg(null);
    try { await axios.delete(`${API_URL}/api/books/companies/${c.id}`, auth()); await reload(); onClose(); }
    catch (e) { setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not remove.' }); }
  };
  const openPdf = async id => {
    const w = window.open('', '_blank');
    try {
      const r = await axios.get(`${API_URL}/api/books/invoices/${id}/pdf`, { ...auth(), responseType: 'blob' });
      w.location.href = URL.createObjectURL(r.data);
    } catch (e) { w.close(); setMsg({ tone: 'bad', text: 'Could not open the PDF from QuickBooks.' }); }
  };

  // Stations this company can take: its own plus any not in another billing company.
  const choices = data.allStations.filter(s => s.billing_company_id === c.id || !s.billing_company_id);
  const custs = data.qboCustomers.filter(q => !q.matched || q.qbo_id === c.qbo_customer_id);

  return (
    <Panel title={c.name} sub={`${c.status} · ${c.stations.length} station${c.stations.length === 1 ? '' : 's'}`} onClose={onClose}
      foot={tab === 'settings' && <><button className="bk-btn ghost" onClick={remove}>Remove company</button><span style={{ flex: 1 }} /><button className="bk-btn primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button></>}>
      <div className="bk-ptabs">
        <button className={tab === 'settings' ? 'on' : ''} onClick={() => setTab('settings')}>Settings</button>
        <button className={tab === 'invoices' ? 'on' : ''} onClick={() => setTab('invoices')}>Invoices &amp; payments</button>
        {(c.pending || []).length > 0 && <button className={tab === 'review' ? 'on' : ''} onClick={() => setTab('review')}>Next invoice</button>}
      </div>
      {c.hold_started_at && <div className="bk-banner bad">On hold since {fmtDate(c.hold_started_at)}: the customer sees masked candidates until the overdue invoice is paid.</div>}
      {msg && <div className={`bk-banner ${msg.tone}`}>{msg.text}</div>}
      {tab === 'settings' && <>
        <div className="bk-grp"><h3>QuickBooks</h3>
          <div className="bk-kv">
            <label htmlFor="b-qb">QuickBooks customer</label>
            <select id="b-qb" value={form.qbo_customer_id || ''} onChange={e => set('qbo_customer_id', e.target.value)}>
              <option value="">Not matched</option>
              {custs.map(q => <option key={q.qbo_id} value={q.qbo_id}>{q.display_name}{q.company_name && q.company_name !== q.display_name ? ` (${q.company_name})` : ''}{q.email ? ` · ${q.email}` : ''}</option>)}
            </select>
            {!form.qbo_customer_id && c.qbo_suggestion && <span className="bk-why">Suggested: <button className="bk-link" onClick={() => set('qbo_customer_id', c.qbo_suggestion.qbo_id)}>{c.qbo_suggestion.display_name}</button></span>}
            {!data.qboCustomers.length && <span className="bk-why">Connect QuickBooks to pick a customer.</span>}
          </div>
        </div>
        <div className="bk-grp"><h3>Who pays</h3>
          <div className="bk-kv">
            <label htmlFor="b-name">Company name</label><input id="b-name" value={form.name || ''} onChange={e => set('name', e.target.value)} />
            <label htmlFor="b-email">Invoice email</label><input id="b-email" value={form.bill_email || ''} onChange={e => set('bill_email', e.target.value)} placeholder={c.qbo_customer?.email || 'from QuickBooks'} />
            <label htmlFor="b-cc">CC</label><input id="b-cc" value={form.cc_emails || ''} onChange={e => set('cc_emails', e.target.value)} placeholder="e.g. hiring@mountainrecruiting.com" />
            <label htmlFor="b-terms">Payment terms</label>
            <select id="b-terms" value={form.terms} onChange={e => set('terms', e.target.value)}>{['Due on receipt', 'Net 7', 'Net 15', 'Net 30'].map(t => <option key={t}>{t}</option>)}</select>
          </div>
        </div>
        <div className="bk-grp"><h3>Price (whole company)</h3>
          <div className="bk-kv">
            <label htmlFor="b-plan">Plan price / month</label><input id="b-plan" inputMode="decimal" value={form.plan_price} onChange={e => set('plan_price', e.target.value)} placeholder="e.g. 899" />
            <span className="bk-why">Covers every station below. Stations don't need their own price.</span>
            <label htmlFor="b-day">Billing day</label>
            <select id="b-day" value={form.billing_day} onChange={e => set('billing_day', e.target.value)}><option value="">Not set</option>{Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{ordinal(d)} of each month</option>)}</select>
            <label htmlFor="b-ads">Charge Indeed ads</label>
            <select id="b-ads" value={form.charge_ads ? 'yes' : 'no'} onChange={e => set('charge_ads', e.target.value === 'yes')}><option value="no">No</option><option value="yes">Yes, last month's actual spend</option></select>
            {form.charge_ads && <><label htmlFor="b-bud">Indeed budget / month</label><input id="b-bud" inputMode="decimal" value={form.ad_budget} onChange={e => set('ad_budget', e.target.value)} placeholder="used to warn on typos" /></>}
          </div>
        </div>
        <div className="bk-grp"><h3>Discounts and credits</h3>
          {c.adjustments.length ? c.adjustments.map(a => (
            <div className="bk-adj" key={a.id}><span><b>{a.percent ? `${Number(a.percent)}%` : money(a.amount)}</b> off · {a.description} <span className="bk-muted">{a.kind === 'ongoing' ? `every invoice${a.until_date ? ' until ' + fmtDate(a.until_date) : ''}` : 'next invoice only'}</span></span><button className="bk-link" onClick={() => delAdj(a.id)}>Remove</button></div>
          )) : <div className="bk-muted">None.</div>}
          <div className="bk-adjadd">
            <select aria-label="Kind" value={adj.kind} onChange={e => setAdj(a => ({ ...a, kind: e.target.value }))}><option value="one_time">Next invoice only</option><option value="ongoing">Every invoice</option></select>
            <input aria-label="Amount" placeholder="$ amount" inputMode="decimal" value={adj.amount} onChange={e => setAdj(a => ({ ...a, amount: e.target.value }))} style={{ width: 90 }} />
            <input aria-label="Shows on invoice as" placeholder="Shows on invoice as, e.g. October missed target" value={adj.description} onChange={e => setAdj(a => ({ ...a, description: e.target.value }))} style={{ flex: 1, minWidth: 160 }} />
            {adj.kind === 'ongoing' && <input aria-label="Until" type="date" value={adj.until_date} onChange={e => setAdj(a => ({ ...a, until_date: e.target.value }))} />}
            <button className="bk-btn" onClick={addAdj}>Add</button>
          </div>
        </div>
        <div className="bk-grp"><h3>Stations covered</h3>
          <div className="bk-stlist">
            {choices.map(s => (
              <label key={s.id} className="bk-stopt"><input type="checkbox" checked={stationIds.includes(s.id)} onChange={e => setStationIds(ids => e.target.checked ? [...ids, s.id] : ids.filter(x => x !== s.id))} /> {s.name} <span className="bk-muted">{s.account_status || ''}</span></label>
            ))}
          </div>
        </div>
        <div className="bk-grp"><h3>When they don't pay</h3>
          <div className="bk-kv">
            <label htmlFor="b-hold">Put candidates on hold</label>
            <select id="b-hold" value={form.hold_policy} onChange={e => set('hold_policy', e.target.value)}><option value="auto">When an invoice is more than 7 days overdue</option><option value="never">Never for this company</option></select>
            <span className="bk-why">Holds aren't switched on yet; this setting is saved for when they are.</span>
            <label htmlFor="b-notes">Notes</label><input id="b-notes" value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
      </>}
      {tab === 'review' && (c.pending || []).map(p => <ReviewInvoice key={p.id} c={c} inv={p} reload={async () => { await reload(); setInv(null); }} setMsg={setMsg} />)}
      {tab === 'invoices' && (
        !inv ? <div className="bk-muted">Loading…</div> : <>
          {!inv.invoices.length && <div className="bk-muted">No invoices yet{c.qbo_customer_id ? '' : '. Match this company to a QuickBooks customer to see its invoices'}.</div>}
          {!!inv.invoices.length && (
            <table className="bk-mini">
              <thead><tr><th>Invoice</th><th>Date</th><th className="n">Total</th><th>Payment</th><th></th></tr></thead>
              <tbody>{inv.invoices.map(i => {
                const late = Number(i.balance) > 0 && i.due_date && String(i.due_date).slice(0, 10) < todayStr();
                return (
                  <tr key={i.id}>
                    <td className="bk-mono">{i.doc_number || (['draft', 'approved', 'sending', 'skipped'].includes(i.status) ? '-' : 'no number')}</td>
                    <td>{fmtDate(i.invoice_date)}</td>
                    <td className="n">{money(i.total)}</td>
                    <td>{i.status === 'void' ? <span className="bk-pill grey">Void</span> : i.status === 'skipped' ? <span className="bk-pill grey">Skipped</span> : ['draft', 'approved', 'sending'].includes(i.status) ? <span className="bk-pill blue">Not sent yet · {i.status === 'draft' ? 'to approve' : 'approved'}</span> : i.balance == null ? <span className="bk-muted">-</span> : Number(i.balance) === 0 ? <span className="bk-pill good">Received</span> : Number(i.balance) < Number(i.total) ? <span className="bk-pill warn">Part · {money(i.balance)} left</span> : <span className={`bk-pill ${late ? 'bad' : 'warn'}`}>{late ? `Overdue since ${fmtDate(i.due_date)}` : 'Not received'}</span>}</td>
                    <td className="bk-acts">{i.qbo_invoice_id && Number(i.balance) > 0 && i.status !== 'void' && <MarkPaid inv={i} reload={async () => { await reload(); setInv(null); }} setMsg={setMsg} />}{i.qbo_invoice_id && <button className="bk-link" onClick={() => openPdf(i.id)}>PDF</button>}{i.invoice_link && <button className="bk-link" onClick={() => navigator.clipboard?.writeText(i.invoice_link)}>Copy pay link</button>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
          {!!inv.payments.length && <>
            <h3 className="bk-h3">Payments</h3>
            <table className="bk-mini"><thead><tr><th>Date</th><th className="n">Amount</th><th>Method</th><th>Paid invoice</th></tr></thead>
              <tbody>{inv.payments.map(p => <tr key={p.id}><td>{fmtDate(p.paid_on)}</td><td className="n">{money(p.amount)}</td><td>{p.method || (p.source === 'manual' ? 'Marked by hand' : 'QuickBooks')}</td><td className="bk-mono">{(p.qbo_invoice_ids || []).map(q => { const m = inv.invoices.find(x => x.qbo_invoice_id === q); return m?.doc_number ? `#${m.doc_number}` : null; }).filter(Boolean).join(', ') || '-'}</td></tr>)}</tbody>
            </table>
          </>}
        </>
      )}
    </Panel>
  );
}

function SetupPanel({ data, onClose, reload }) {
  const [rows, setRows] = useState(() => data.suggestions.map((s, i) => ({ key: i, ...s, plan_price: s.plan_price ?? '', billing_day: s.billing_day ?? '', pick: true })));
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (key, k, v) => setRows(rs => rs.map(r => (r.key === key ? { ...r, [k]: v } : r)));
  const create = async onlyKey => {
    const groups = rows.filter(r => (onlyKey == null ? r.pick : r.key === onlyKey)).map(r => ({ name: r.name, station_ids: r.station_ids, plan_price: r.plan_price, billing_day: r.billing_day, charge_ads: r.charge_ads }));
    if (!groups.length) return;
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API_URL}/api/books/companies`, { groups }, auth());
      const done = new Set(groups.flatMap(g => g.station_ids));
      setRows(rs => rs.filter(x => !x.station_ids.some(id => done.has(id))));
      setMsg({ tone: 'good', text: `Created ${r.data.created.length} compan${r.data.created.length === 1 ? 'y' : 'ies'}.` });
      await reload();
    } catch (e) {
      setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not create them.' });
    } finally {
      setBusy(false);
    }
  };
  const picked = rows.filter(r => r.pick).length;
  return (
    <Panel title="Set up billing companies" sub="Each row is one company that pays. Stations that belong to the same customer are grouped; check before creating." onClose={onClose}
      foot={<><span className="bk-muted">{picked} selected</span><span style={{ flex: 1 }} /><button className="bk-btn primary" disabled={busy || !picked} onClick={() => create(null)}>{busy ? 'Creating…' : `Create ${picked} selected`}</button></>}>
      {msg && <div className={`bk-banner ${msg.tone}`}>{msg.text}</div>}
      {!rows.length && <div className="bk-muted">Everything is set up.</div>}
      {rows.map(r => (
        <div className={`bk-sug ${r.station_ids.length > 1 ? 'multi' : ''}`} key={r.key}>
          <div className="bk-sug-top">
            <input type="checkbox" checked={r.pick} onChange={e => upd(r.key, 'pick', e.target.checked)} aria-label={`Select ${r.name}`} />
            <input className="bk-sug-name" value={r.name} onChange={e => upd(r.key, 'name', e.target.value)} aria-label="Company name" />
            <button className="bk-link" onClick={() => create(r.key)} disabled={busy}>Create</button>
          </div>
          <div className="bk-sug-st">{r.stations.map(s => <span key={s.id} className="bk-chip-s">{s.name}{s.pricing != null ? ` · $${Number(s.pricing)}` : ''}{s.account_status && s.account_status !== 'Live' ? ` · ${s.account_status}` : ''}</span>)}</div>
          <div className="bk-sug-f">
            <label>Plan / mo <input inputMode="decimal" value={r.plan_price} onChange={e => upd(r.key, 'plan_price', e.target.value)} placeholder="set later" /></label>
            <label>Billing day <input inputMode="numeric" value={r.billing_day} onChange={e => upd(r.key, 'billing_day', e.target.value)} style={{ width: 50 }} /></label>
            <label><input type="checkbox" checked={!!r.charge_ads} onChange={e => upd(r.key, 'charge_ads', e.target.checked)} /> Indeed ads</label>
          </div>
          {r.price_note && <div className="bk-why">{r.price_note}. Set the company price.</div>}
        </div>
      ))}
    </Panel>
  );
}

function LogPanel({ onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/books/qbo/log`, auth()).then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <Panel title="QuickBooks sync log" sub="Last 200 events. intuit_tid identifies a request for Intuit support." onClose={onClose}>
      {!rows ? <div className="bk-muted">Loading…</div> : !rows.length ? <div className="bk-muted">Nothing logged yet.</div> : (
        <table className="bk-mini"><thead><tr><th>Time (IST)</th><th>What</th><th>Detail</th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} className={r.ok ? '' : 'bad'}>
              <td>{new Date(r.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', second: '2-digit' })}</td>
              <td>{r.ok ? '' : '⚠ '}{r.kind}</td>
              <td className="bk-logd">{r.detail}{r.intuit_tid && <span className="bk-sub">intuit_tid {r.intuit_tid}</span>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </Panel>
  );
}

// Each row follows this cycle's invoice start to finish (server: cycleOf in routes/books.js),
// then the last money received and the next billing date (user, 3 Nov 2026).
function CycleCells({ c }) {
  const cy = c.cycle;
  if (!cy) return <><td className="bk-muted">not set</td><td></td><td></td><td></td><td></td></>;
  const i = cy.invoice;
  const today = todayStr();
  let st;
  if (!i && !c.qbo_customer_id) st = <><span className="bk-pill grey">Match QuickBooks</span><span className="bk-sub">can't see its invoices yet</span></>;
  else if (!i) st = cy.date > today ? <><span className="bk-pill grey">Not prepared</span><span className="bk-sub">draft appears 7 days before</span></> : <><span className="bk-pill bad">Not invoiced</span><span className="bk-sub">no invoice for this date</span></>;
  else if (i.status === 'draft') st = <><span className="bk-pill warn">To approve</span><span className="bk-sub">{i.ready === false ? (i.why === 'Enter the Indeed ad spend' ? 'needs ad spend' : i.why) : money(i.total)}</span></>;
  else if (i.status === 'approved') st = <><span className="bk-pill good">Approved</span><span className="bk-sub">{sendWhen(i.invoice_date).short} · {money(i.total)}</span></>;
  else if (i.status === 'sending') st = <span className="bk-pill blue">Sending…</span>;
  else if (i.status === 'created') st = <><span className="bk-pill bad">Email failed</span><span className="bk-sub">open to send again</span></>;
  else if (i.status === 'skipped') st = <span className="bk-pill grey">Skipped</span>;
  else st = <><span className="bk-pill blue">Sent</span><span className="bk-sub">{i.doc_number ? `#${i.doc_number} · ` : ''}{money(i.total)}</span></>;
  let pay = <span className="bk-muted">-</span>;
  if (i && ['sent', 'created'].includes(i.status) && i.balance != null) {
    const days = i.due_date && i.due_date < today ? Math.round((new Date(today) - new Date(i.due_date)) / 86400000) : 0;
    pay = i.balance <= 0 ? <><span className="bk-pill good">Paid</span>{i.paid_on && <span className="bk-sub">{fmtDate(i.paid_on)}</span>}</>
      : i.balance < i.total ? <><span className="bk-pill warn">Part paid</span><span className="bk-sub">{money(i.balance)} left</span></>
      : days > 0 ? <><span className="bk-pill bad">Overdue {days}d</span><span className="bk-sub">{money(i.balance)}</span></>
      : <><span className="bk-pill warn">Not paid</span><span className="bk-sub">due {fmtDate(i.due_date)}</span></>;
  }
  const lp = c.latest_payment;
  return <>
    <td>{fmtDate(cy.date)}</td>
    <td>{st}</td>
    <td>{pay}{cy.older && <span className="bk-sub bad">+ {money(cy.older.owed)} older unpaid</span>}</td>
    <td>{lp ? <>{fmtDate(lp.paid_on)} · {money(lp.amount)}<span className="bk-sub">{[lp.method, ...(c.last_payment_docs || []).map(d => `#${d}`)].filter(Boolean).join(' · ')}</span></> : <span className="bk-muted">none</span>}</td>
    <td>{fmtDate(cy.next_billing)}</td>
  </>;
}

function ReviewInvoice({ c, inv, reload, setMsg }) {
  const [lines, setLines] = useState(inv.lines || []);
  const [busy, setBusy] = useState('');
  const [confirmAds, setConfirmAds] = useState(false);
  useEffect(() => { setLines(inv.lines || []); setConfirmAds(false); }, [inv]);
  const editable = ['draft', 'approved'].includes(inv.status);
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const num = v => (v === '' || v == null ? null : Number(String(v).replace(/[$,\s]/g, '')));
  const total = lines.reduce((t, l) => t + (num(l.amount) == null ? 0 : (l.kind === 'discount' ? -Math.abs(num(l.amount)) : num(l.amount))), 0);
  const call = async (what, fn) => {
    setBusy(what); setMsg(null);
    try { await fn(); await reload(); }
    catch (e) {
      if (e.response?.data?.code === 'ads_check') setConfirmAds(true);
      setMsg({ tone: 'bad', text: e.response?.data?.error || 'Something went wrong.' });
    } finally { setBusy(''); }
  };
  const save = () => axios.patch(`${API_URL}/api/books/invoices/${inv.id}`, { lines: lines.map(l => ({ ...l, amount: num(l.amount) })) }, auth());
  const changed = JSON.stringify(lines) !== JSON.stringify(inv.lines || []);
  const labels = { plan: 'Full Service Recruiting', ads: 'Indeed Ad Spend', discount: 'Discount', charge: 'Full Service Recruiting' };
  return (
    <div className="bk-grp">
      <h3>Invoice for {fmtDate(inv.invoice_date)} · {inv.status === 'draft' ? 'draft, not sent' : inv.status === 'approved' ? `approved, ${sendWhen(inv.invoice_date).long}` : inv.status === 'created' ? 'in QuickBooks, email failed' : inv.status}</h3>
      {inv.send_error && <div className="bk-banner bad">{inv.send_error}</div>}
      <div className="bk-inv">
        <div className="bk-inv-top"><div><b className="bk-inv-title">INVOICE</b><br />Mountain Group LLC</div><div className="bk-muted">Invoice no. assigned by QuickBooks<br />Terms: {c.terms || 'Due on receipt'} · Due {fmtDate(inv.due_date)}</div></div>
        <div className="bk-inv-bill"><span className="bk-muted">Bill to</span> {c.qbo_customer?.display_name || c.name}<span className="bk-sub">{c.bill_email || c.qbo_customer?.email || 'no invoice email set'}{c.cc_emails ? ` · cc ${c.cc_emails}` : ''}</span></div>
        <table className="bk-mini">
          <thead><tr><th>Product or service</th><th>Description</th><th className="n">Amount</th><th></th></tr></thead>
          <tbody>{lines.map((l, i) => (
            <tr key={i}>
              <td>{labels[l.kind]}</td>
              <td>{editable ? <input className="bk-in" value={l.description || ''} onChange={e => setLine(i, 'description', e.target.value)} aria-label="Description" /> : l.description}</td>
              <td className="n">{editable ? <input className={`bk-in n ${l.kind === 'ads' && (l.amount === null || l.amount === '') ? 'need' : ''}`} value={l.amount == null ? '' : (l.kind === 'discount' ? Math.abs(l.amount) : l.amount)} placeholder={l.kind === 'ads' ? 'enter' : ''} inputMode="decimal" onChange={e => setLine(i, 'amount', e.target.value)} aria-label="Amount" /> : money(l.amount)}{l.kind === 'discount' && editable && <span className="bk-muted"> off</span>}</td>
              <td>{editable && !['plan', 'ads'].includes(l.kind) && <button className="bk-link" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>Remove</button>}</td>
            </tr>
          ))}</tbody>
        </table>
        {editable && <div className="bk-adjadd"><button className="bk-link" onClick={() => setLines(ls => [...ls, { kind: 'charge', description: '', amount: '' }])}>+ Extra charge</button><button className="bk-link" onClick={() => setLines(ls => [...ls, { kind: 'discount', description: '', amount: '' }])}>+ Credit / discount</button></div>}
        <div className="bk-inv-total">Total <b>{money(Math.round(total * 100) / 100) || '$0'}</b></div>
      </div>
      {c.charge_ads && c.ad_budget ? <div className="bk-muted">Indeed budget {money(c.ad_budget)} / month; spend over {money(c.ad_budget * 1.5)} asks you to confirm.</div> : null}
      <div className="bk-row-acts">
        {editable && changed && <button className="bk-btn" disabled={!!busy} onClick={() => call('save', save)}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button>}
        {inv.status === 'draft' && <button className="bk-btn" disabled={!!busy || changed} title={changed ? 'Save changes first' : ''} onClick={() => call('approve', () => axios.post(`${API_URL}/api/books/invoices/${inv.id}/approve`, { confirmAds }, auth()))}>{busy === 'approve' ? 'Approving…' : confirmAds ? 'Spend is right, approve' : `Approve (${sendWhen(inv.invoice_date).short})`}</button>}
        {['draft', 'approved', 'created'].includes(inv.status) && <button className="bk-btn primary" disabled={!!busy || changed} title={changed ? 'Save changes first' : ''} onClick={() => call('send', () => axios.post(`${API_URL}/api/books/invoices/${inv.id}/send`, { confirmAds }, auth()))}>{busy === 'send' ? 'Sending…' : inv.status === 'created' ? 'Send email again' : confirmAds ? 'Spend is right, send now' : 'Send now'}</button>}
        {inv.status === 'approved' && <button className="bk-btn ghost" disabled={!!busy} onClick={() => call('unapprove', () => axios.post(`${API_URL}/api/books/invoices/${inv.id}/unapprove`, {}, auth()))}>Back to draft</button>}
        {['draft', 'approved'].includes(inv.status) && <button className="bk-btn ghost" disabled={!!busy} onClick={() => call('skip', () => axios.post(`${API_URL}/api/books/invoices/${inv.id}/skip`, {}, auth()))}>Skip this month</button>}
      </div>
    </div>
  );
}

function MarkPaid({ inv, reload, setMsg }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ amount: inv.balance, method: 'Check', paid_on: new Date().toISOString().slice(0, 10), note: '' });
  const [busy, setBusy] = useState(false);
  if (!open) return <button className="bk-link" onClick={() => setOpen(true)}>Mark received</button>;
  const go = async () => {
    setBusy(true); setMsg(null);
    try { await axios.post(`${API_URL}/api/books/invoices/${inv.id}/mark-paid`, f, auth()); setMsg({ tone: 'good', text: `Recorded in QuickBooks: ${money(f.amount)} by ${f.method}.` }); setOpen(false); await reload(); }
    catch (e) { setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not record it.' }); }
    finally { setBusy(false); }
  };
  return (
    <span className="bk-adjadd">
      <input className="bk-in n" style={{ width: 80 }} value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} aria-label="Amount" />
      <select value={f.method} onChange={e => setF({ ...f, method: e.target.value })} aria-label="Method"><option>Check</option><option>Wire</option><option>Zelle</option><option>Cash</option><option>Other</option></select>
      <input type="date" value={f.paid_on} onChange={e => setF({ ...f, paid_on: e.target.value })} aria-label="Date received" />
      <button className="bk-btn primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'Record'}</button>
      <button className="bk-link" onClick={() => setOpen(false)}>Cancel</button>
    </span>
  );
}

function SettingsPanel({ onClose, reload }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API_URL}/api/books/settings`, auth()).then(r => setS(r.data)).catch(() => setMsg({ tone: 'bad', text: 'Could not load settings.' })), []);
  useEffect(() => { load(); }, [load]);
  const flip = async (k, v) => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.post(`${API_URL}/api/books/settings`, { [k]: v }, auth());
      const rep = r.data.report || {};
      setMsg({ tone: 'good', text: `Saved.${rep.held?.length ? ` On hold now: ${rep.held.join(', ')}.` : ''}${rep.released?.length ? ` Released: ${rep.released.join(', ')}.` : ''}` });
      await load(); await reload();
    } catch (e) { setMsg({ tone: 'bad', text: e.response?.data?.error || 'Could not save.' }); }
    finally { setBusy(false); }
  };
  const release = async id => {
    setBusy(true);
    try { await axios.post(`${API_URL}/api/books/companies/${id}/release-hold`, { days: 7 }, auth()); setMsg({ tone: 'good', text: 'Hold released for 7 days.' }); await load(); await reload(); }
    catch (e) { setMsg({ tone: 'bad', text: 'Could not release it.' }); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Holds and reminders" sub="Both start off. Turn them on when you're ready." onClose={onClose}>
      {msg && <div className={`bk-banner ${msg.tone}`}>{msg.text}</div>}
      {!s ? <div className="bk-muted">Loading…</div> : <>
        <div className="bk-grp"><h3>Candidate holds</h3>
          <label className="bk-switch"><input type="checkbox" disabled={busy} checked={s.holds_enabled} onChange={e => flip('holds_enabled', e.target.checked)} /> Put candidates on hold when an invoice is more than 7 days overdue</label>
          <div className="bk-muted">While on hold, recruiters keep sending; the customer's Ready for Interview email and text show masked names with "pay invoice to see who they are" and the pay link; their dashboard and resume links don't show those candidates. When QuickBooks reports the payment, the hold lifts and one email lists everyone held. A part payment lifts the hold. Companies set to "Never" are skipped.</div>
          <div className="bk-hold-list"><b>{s.holds_enabled ? 'On hold now' : 'Would be on hold if you turn this on'}:</b> {(s.holds_enabled ? s.onHold.map(h => h.name) : s.wouldHold).join(', ') || 'nobody'}</div>
          {s.onHold.map(h => <div className="bk-adj" key={h.id}><span>{h.name} <span className="bk-muted">on hold since {fmtDate(h.hold_started_at)}</span></span><button className="bk-link" disabled={busy} onClick={() => release(h.id)}>Release for 7 days</button></div>)}
        </div>
        <div className="bk-grp"><h3>Payment reminders</h3>
          <label className="bk-switch"><input type="checkbox" disabled={busy} checked={s.reminders_enabled} onChange={e => flip('reminders_enabled', e.target.checked)} /> Email a reminder 3 and 6 days after the due date</label>
          <div className="bk-muted">Sent to the invoice email (CC hiring@) with the pay link. The day-6 one says new candidates will be held from the next day, when holds are on.</div>
        </div>
        <div className="bk-grp"><h3>QuickBooks products</h3>
          {s.items.missing.length ? <div className="bk-banner bad">Not found in QuickBooks: {s.items.missing.join(', ')}. Invoices can't be created until these exist (names must match).</div> : <div className="bk-muted">Using Full Service Recruiting, Indeed Ad Spend and Discount.</div>}
        </div>
      </>}
    </Panel>
  );
}
